import PropTypes from 'prop-types';
import { Button } from '@aimsos/ui';

import { ArrowLeft, ArrowRight } from 'lucide-react';

/**
 * Sticky bottom navigation for the builder (CT-5190, added 2026-09-08).
 *
 * The design system specifies a **Stepper Nav Footer**: a 72px bar pinned to the
 * bottom of the scrollport with a top separator, `Cancel` (secondary, first
 * step) or `Back` (secondary + ArrowLeft) on the left, and a primary
 * right-aligned action with ArrowRight whose label changes on the last step,
 * disabled via `nextDisabled`.
 *
 * That component is **not published in `@aimsos/ui` v1.4.1**, so this is the
 * spec's anatomy composed from the DS parts that do exist — `Button` for both
 * controls, surface/edge tokens for the bar. Swap the whole file for the real
 * component when the design system ships it; nothing else imports its internals.
 *
 * It wears the paginator's container, because on this page it *is* the
 * paginator's counterpart: the same rounded, bordered, translucent-and-blurred
 * bar pinned with `sticky bottom-0 z-10 mt-auto`. `Pagination` keeps that chrome
 * private (`Pagination.jsx:11`, not exported), so it is duplicated below — the
 * same "copy the pattern locally" rule this domain already follows for
 * `ModalFooter` and `previewSlideOutShared`.
 *
 * Deliberately **not** `-mx-6`: that trick belongs to the sticky bars at the
 * *top* of a list page. Every bottom bar in Governance and Agentic Studio stays
 * inside the page gutter, which is what makes it read as a floating card rather
 * than an edge-to-edge band.
 *
 * It renders as a sibling of the two-column layout, not inside the
 * configuration column, so it spans the whole page the way the paginator does.
 */

/**
 * `Pagination`'s own container, verbatim, with `justify-end gap-10` swapped for
 * this bar's `justify-between gap-3`. Replace with the DS export the day
 * `@aimsos/ui` publishes one.
 */
const NAV_FOOTER_BAR_CLASS = [
  'sticky bottom-0 z-10 mt-auto shrink-0',
  'flex w-full items-center justify-between gap-3 px-3 py-2',
  'rounded-xl border border-edge',
  'bg-[rgba(255,255,255,0.92)] backdrop-blur-[8px] shadow-[0px_4px_12px_rgba(0,0,0,0.08)]',
  'dark:bg-[rgba(20,27,42,0.85)] dark:shadow-[0px_4px_12px_rgba(0,0,0,0.35)]',
].join(' ');

function WidgetBuilderNavFooter({
  variant,
  ariaLabel,
  backLabel,
  nextLabel,
  nextDisabled = false,
  nextLoading = false,
  nextTitle,
  busy = false,
  onBack,
  onNext,
}) {
  return (
    <div
      role="navigation"
      aria-label={ariaLabel}
      className={NAV_FOOTER_BAR_CLASS}
    >
      <Button
        variant="secondary"
        leftIcon={variant === 'back-next' ? ArrowLeft : undefined}
        disabled={busy}
        onClick={onBack}
      >
        {backLabel}
      </Button>

      <Button
        variant="primary"
        rightIcon={variant === 'back-next' ? undefined : ArrowRight}
        disabled={nextDisabled || busy}
        loading={nextLoading}
        title={nextTitle}
        onClick={onNext}
      >
        {nextLabel}
      </Button>
    </div>
  );
}

WidgetBuilderNavFooter.propTypes = {
  /** `cancel-next` on the first tab, `back-next` on the last. */
  variant: PropTypes.oneOf(['cancel-next', 'back-next']).isRequired,
  ariaLabel: PropTypes.string.isRequired,
  backLabel: PropTypes.string.isRequired,
  nextLabel: PropTypes.string.isRequired,
  nextDisabled: PropTypes.bool,
  nextLoading: PropTypes.bool,
  nextTitle: PropTypes.string,
  /** A save is running: both actions wait for it, so neither looks available. */
  busy: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default WidgetBuilderNavFooter;
